/**
 * useSpectraListeners Hook
 * 
 * React hook for managing spectra-specific listeners.
 * Subscribes when entering spectra, unsubscribes when leaving.
 */

import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import listenerManager from './index';

/**
 * Hook to manage spectra listeners for a specific spectra
 * 
 * @param {Object} space - Spectra object with space_id, owner_id, etc.
 */
export const useSpectraListeners = (space) => {
  const navigate = useNavigate();
  const params = useParams();

  useEffect(() => {
    if (!space) {
      return;
    }

    let cancelled = false;

    // Update context with current params/navigate immediately (safe pre-init).
    listenerManager.updateContext({
      params,
      navigate,
      space,
    });

    // Wait for the ListenerManager to finish initializing before subscribing.
    // Without this, opening a Spectra spark on a fresh app load races
    // initialize() and logs "Not initialized. Call initialize() first." — the
    // subscribe silently no-ops, so Spectra real-time events never attach until
    // a later navigation. Same fix already applied to
    // useChatMetadataListeners/useDashboardListeners.
    listenerManager.whenInitialized().then(() => {
      if (cancelled) return;
      listenerManager.subscribeSpectra(space);
    });

    // Cleanup on unmount or space change
    return () => {
      cancelled = true;
      listenerManager.unsubscribeSpectra(space);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [space?.space_id]); // Intentional: re-subscribe only when space identity changes

  // Update context when navigation changes (without re-subscribing)
  useEffect(() => {
    if (space) {
      listenerManager.updateContext({
        params,
        navigate,
        space,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, navigate, space?.space_id]); // Intentional: space identity is the stable key

  return null;
};

export default useSpectraListeners;
